/**
 * Employment contracts store module (namespaced: true).
 *
 * Договоры водителя Яндекса: шаблоны компании, генерация PDF,
 * подпись предприятия через NCALayer/Sigex, подписание водителем через eGov.
 *
 * Access:
 *   this.$store.dispatch('employmentContracts/fetchDriverReference')
 *   this.$store.getters['employmentContracts/driverReference']
 */
import api from '@api'

const unwrap = (data) => (data && Object.prototype.hasOwnProperty.call(data, 'response') ? data.response : data)

const initialLoadingFlags = () => ({
    isLoadingTemplateReference: false,
    isLoadingDriverReference: false,
    isLoadingContracts: false,
    isPreviewing: false,
    isGenerating: false,
    isPreparingEnterpriseSign: false,
    isWaitingNcALayer: false,
    isSubmittingEnterpriseSign: false,
    isSendingToDriver: false,
    isPollingDriverSign: false,
    isDownloading: false,
})

const initialState = () => ({
    templateReference: null,
    driverReference: null,
    driverReferenceKey: null,
    contracts: [],
    contractsKey: null,
    pollingIntervalId: null,
    pollingContractId: null,
    ...initialLoadingFlags(),
})

const contextKey = (driverId, parkId) => (driverId && parkId != null ? `${driverId}::${parkId}` : null)

export default {
    namespaced: true,
    state: initialState(),
    mutations: {
        SET_FLAG(state, { key, value }) {
            state[key] = value
        },
        SET_TEMPLATE_REFERENCE(state, data) {
            state.templateReference = data
        },
        SET_DRIVER_REFERENCE(state, { data, key }) {
            state.driverReference = data
            state.driverReferenceKey = key
        },
        SET_CONTRACTS(state, { data, key }) {
            state.contracts = Array.isArray(data) ? data : []
            state.contractsKey = key
        },
        UPSERT_CONTRACT(state, contract) {
            if (!contract || !contract.id) return
            const idx = state.contracts.findIndex((c) => c.id === contract.id)
            if (idx === -1) {
                state.contracts = [...state.contracts, contract]
            } else {
                const next = state.contracts.slice()
                next.splice(idx, 1, { ...next[idx], ...contract })
                state.contracts = next
            }
        },
        SET_POLLING(state, { intervalId, contractId }) {
            state.pollingIntervalId = intervalId
            state.pollingContractId = contractId
        },
        RESET(state) {
            const fresh = initialState()
            Object.keys(fresh).forEach((k) => {
                state[k] = fresh[k]
            })
        },
    },
    actions: {
        async fetchTemplateReference({ commit, state }, { force = false } = {}) {
            if (state.templateReference && !force) return state.templateReference
            commit('SET_FLAG', { key: 'isLoadingTemplateReference', value: true })
            try {
                const data = await api.employmentContracts.templatesReference()
                commit('SET_TEMPLATE_REFERENCE', data)
                return data
            } finally {
                commit('SET_FLAG', { key: 'isLoadingTemplateReference', value: false })
            }
        },

        async fetchDriverReference({ commit, state }, { driverId, parkId, force = false } = {}) {
            const key = contextKey(driverId, parkId)
            if (state.driverReference && state.driverReferenceKey === key && !force) {
                return state.driverReference
            }
            commit('SET_FLAG', { key: 'isLoadingDriverReference', value: true })
            try {
                const data = await api.employmentContracts.driverReference()
                commit('SET_DRIVER_REFERENCE', { data, key })
                return data
            } finally {
                commit('SET_FLAG', { key: 'isLoadingDriverReference', value: false })
            }
        },

        async fetchDriverContracts({ commit }, { driverId, parkId }) {
            const key = contextKey(driverId, parkId)
            commit('SET_FLAG', { key: 'isLoadingContracts', value: true })
            try {
                const data = await api.employmentContracts.driverContracts({ driver_id: driverId, park_id: parkId })
                const list = unwrap(data)
                commit('SET_CONTRACTS', { data: list, key })
                return list
            } finally {
                commit('SET_FLAG', { key: 'isLoadingContracts', value: false })
            }
        },

        async fetchDriverContract({ commit }, contractId) {
            const data = await api.employmentContracts.driverContracts(null, `/${contractId}`)
            const contract = unwrap(data)
            commit('UPSERT_CONTRACT', contract)
            return contract
        },

        async preview({ commit }, payload) {
            commit('SET_FLAG', { key: 'isPreviewing', value: true })
            try {
                const data = await api.employmentContracts.preview(payload)
                return unwrap(data)
            } finally {
                commit('SET_FLAG', { key: 'isPreviewing', value: false })
            }
        },

        async generate({ commit }, payload) {
            commit('SET_FLAG', { key: 'isGenerating', value: true })
            try {
                const data = await api.employmentContracts.generate(payload)
                const contract = unwrap(data)
                commit('UPSERT_CONTRACT', contract)
                return contract
            } finally {
                commit('SET_FLAG', { key: 'isGenerating', value: false })
            }
        },

        // ТЗ §13 — отдельное состояние ожидания NCALayer.
        // NCALayer-подпись оркестрируется в компоненте между getFile и signEnterprise,
        // поэтому флаг выставляет компонент через этот action.
        setNcALayerWaiting({ commit }, value) {
            commit('SET_FLAG', { key: 'isWaitingNcALayer', value: Boolean(value) })
        },

        async getFile({ commit }, contractId) {
            commit('SET_FLAG', { key: 'isPreparingEnterpriseSign', value: true })
            try {
                const data = await api.employmentContracts.getFile(null, `/${contractId}`)
                return unwrap(data)
            } finally {
                commit('SET_FLAG', { key: 'isPreparingEnterpriseSign', value: false })
            }
        },

        async signEnterprise({ commit }, payload) {
            commit('SET_FLAG', { key: 'isSubmittingEnterpriseSign', value: true })
            try {
                const data = await api.employmentContracts.signEnterprise(payload)
                return unwrap(data)
            } finally {
                commit('SET_FLAG', { key: 'isSubmittingEnterpriseSign', value: false })
            }
        },

        async signDriver({ commit }, payload) {
            commit('SET_FLAG', { key: 'isSendingToDriver', value: true })
            try {
                const data = await api.employmentContracts.signDriver(payload)
                return unwrap(data)
            } finally {
                commit('SET_FLAG', { key: 'isSendingToDriver', value: false })
            }
        },

        async downloadDocument({ commit }, contractId) {
            commit('SET_FLAG', { key: 'isDownloading', value: true })
            try {
                const data = await api.employmentContracts.downloadDocument(null, `/${contractId}`)
                return unwrap(data)
            } finally {
                commit('SET_FLAG', { key: 'isDownloading', value: false })
            }
        },

        startPolling({ commit, dispatch, state }, { contractId, intervalMs = 7000, onSigned = null } = {}) {
            dispatch('stopPolling')
            if (!contractId) return
            commit('SET_FLAG', { key: 'isPollingDriverSign', value: true })
            const tick = async () => {
                try {
                    const contract = await dispatch('fetchDriverContract', contractId)
                    const signed = contract && (contract.status === 'signed' || (contract.driver_sign && contract.driver_sign.status === 'sign'))
                    if (signed) {
                        dispatch('stopPolling')
                        if (typeof onSigned === 'function') onSigned(contract)
                    }
                } catch (err) {
                    console.error('[employmentContracts] polling failed', err)
                }
            }
            const intervalId = setInterval(tick, intervalMs)
            commit('SET_POLLING', { intervalId, contractId })
        },

        stopPolling({ commit, state }) {
            if (state.pollingIntervalId) {
                clearInterval(state.pollingIntervalId)
            }
            commit('SET_POLLING', { intervalId: null, contractId: null })
            commit('SET_FLAG', { key: 'isPollingDriverSign', value: false })
        },

        reset({ commit, dispatch }) {
            dispatch('stopPolling')
            commit('RESET')
        },
    },
    getters: {
        templateReference: (s) => s.templateReference,
        driverReference: (s) => s.driverReference,
        contracts: (s) => s.contracts,
        contractByType: (s) => (type) => s.contracts.find((c) => c.template && c.template.type === type) || null,
        isBusy: (s) => (
            s.isLoadingDriverReference ||
            s.isLoadingContracts ||
            s.isPreviewing ||
            s.isGenerating ||
            s.isPreparingEnterpriseSign ||
            s.isWaitingNcALayer ||
            s.isSubmittingEnterpriseSign ||
            s.isSendingToDriver ||
            s.isDownloading
        ),
    },
}